import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import { Row, Col, Spinner, Alert, Table, Card, Button, Modal, Badge } from "react-bootstrap";
import axios from "axios";
import { TranslatorContext } from "../../../../context/Translator";
import LabelFieldComponent from "../../../../components/fields/LabelFieldComponent";
import PageLayout from "../../../../layouts/PageLayout";
import AddPaymentModal from "../AddPaymentModal";

export default function CheckerDetailPage() {
    const { id: checkerId } = useParams();
    const { t } = useContext(TranslatorContext);

    // States
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);
    const [activeTab, setActiveTab] = useState("products"); // 'products' | 'payments'

    // Data States
    const [checkerInfo, setCheckerInfo] = useState(null);
    const [products, setProducts] = useState([]);
    const [payments, setPayments] = useState([]);
    const [banks, setBanks] = useState([]);
    const [search, setSearch] = useState("");

    // Modals
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    const imgBaseUrl = process.env.REACT_APP_IMG_URL || "http://localhost:8000/storage/";

    const resolveImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${imgBaseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    };

    // Fetch Checker Details
    const fetchCheckerDetails = useCallback(async (searchQuery = "") => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/checker/show/${checkerId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: searchQuery ? { search: searchQuery } : {},
                }
            );

            if (response.data?.success) {
                setCheckerInfo(response.data.checker || null);
                setProducts(response.data.products || []);
                setPayments(response.data.payments || []);
                setBanks(response.data.banks || []);
            }
        } catch (err) {
            console.error("Error fetching checker details:", err);
            setError(err.response?.data?.message || "Failed to fetch checker details.");
        } finally {
            setLoading(false);
        }
    }, [checkerId]);

    useEffect(() => {
        fetchCheckerDetails();
    }, [fetchCheckerDetails]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        fetchCheckerDetails(val);
    };

    const handlePaymentSuccess = (msg) => {
        setSuccessMsg(msg);
        fetchCheckerDetails(search);
    };

    return (
        <PageLayout>
            {/* Header / Breadcrumb */}
            <Row className="mb-4">
                <Col xl={12}>
                    <div
                        className="mc-card p-4 rounded-4 shadow-sm border-0 d-flex justify-content-between align-items-center flex-wrap gap-3"
                        style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", color: "#fff" }}
                    >
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <span className="material-icons text-primary fs-3">fact_check</span>
                                <h3 className="mc-breadcrumb-title m-0 text-white fw-bold">
                                    {checkerInfo?.name || "Checker Profile"}
                                </h3>
                            </div>
                            <p className="text-white-50 small m-0 ms-4">{checkerInfo?.email || "Checker Checked Products & Earnings"}</p>
                            <ul className="mc-breadcrumb-list mt-2 ms-4">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link text-white-50">{t("Home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="/checkers" className="mc-breadcrumb-link text-white-50">{t("Checkers")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item text-primary fw-semibold">{t("Details")}</li>
                            </ul>
                        </div>
                        <Button
                            variant="success"
                            className="d-flex align-items-center gap-2 px-4 py-2 rounded-3 shadow fw-bold text-white border-0"
                            onClick={() => setShowPaymentModal(true)}
                            disabled={loading}
                            style={{ backgroundColor: "#10b981" }}
                        >
                            <span className="material-icons">add_card</span>
                            {t("Add Payment")}
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* Notifications */}
            {error && <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-4 shadow-sm rounded-3">{error}</Alert>}
            {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg("")} className="mb-4 shadow-sm rounded-3">{successMsg}</Alert>}

            {/* Stat Cards Summary */}
            <Row className="g-3 mb-4">
                <Col sm={6} md={4} xl={2.4}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" style={{ background: "#f8fafc" }}>
                        <Card.Body className="d-flex align-items-center p-3 gap-3">
                            <div className="rounded-3 p-3 text-primary d-flex align-items-center justify-content-center" style={{ background: "#e0f2fe" }}>
                                <span className="material-icons">sell</span>
                            </div>
                            <div>
                                <span className="text-muted text-uppercase fw-semibold small d-block mb-1">{t("Rate / Item")}</span>
                                <h4 className="fw-bold m-0 text-dark">{checkerInfo?.per_product_amount?.toFixed(2) || "0.00"}</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col sm={6} md={4} xl={2.4}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" style={{ background: "#f8fafc" }}>
                        <Card.Body className="d-flex align-items-center p-3 gap-3">
                            <div className="rounded-3 p-3 text-info d-flex align-items-center justify-content-center" style={{ background: "#e0e7ff" }}>
                                <span className="material-icons">verified</span>
                            </div>
                            <div>
                                <span className="text-muted text-uppercase fw-semibold small d-block mb-1">{t("Checked Items")}</span>
                                <h4 className="fw-bold m-0 text-dark">{checkerInfo?.completed_products || 0}</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col sm={6} md={4} xl={2.4}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" style={{ background: "#f8fafc" }}>
                        <Card.Body className="d-flex align-items-center p-3 gap-3">
                            <div className="rounded-3 p-3 text-dark d-flex align-items-center justify-content-center" style={{ background: "#f1f5f9" }}>
                                <span className="material-icons">payments</span>
                            </div>
                            <div>
                                <span className="text-muted text-uppercase fw-semibold small d-block mb-1">{t("Total Earned")}</span>
                                <h4 className="fw-bold m-0 text-dark">{checkerInfo?.total_amount?.toFixed(2) || "0.00"}</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col sm={6} md={4} xl={2.4}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" style={{ background: "#f8fafc" }}>
                        <Card.Body className="d-flex align-items-center p-3 gap-3">
                            <div className="rounded-3 p-3 text-success d-flex align-items-center justify-content-center" style={{ background: "#dcfce7" }}>
                                <span className="material-icons">task_alt</span>
                            </div>
                            <div>
                                <span className="text-muted text-uppercase fw-semibold small d-block mb-1">{t("Paid Amount")}</span>
                                <h4 className="fw-bold m-0 text-success">{checkerInfo?.paid_amount?.toFixed(2) || "0.00"}</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col sm={6} md={4} xl={2.4}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" style={{ background: "#fff1f2", borderLeft: "4px solid #f43f5e" }}>
                        <Card.Body className="d-flex align-items-center p-3 gap-3">
                            <div className="rounded-3 p-3 text-danger d-flex align-items-center justify-content-center" style={{ background: "#ffe4e6" }}>
                                <span className="material-icons">pending_actions</span>
                            </div>
                            <div>
                                <span className="text-danger text-uppercase fw-bold small d-block mb-1">{t("Remaining")}</span>
                                <h4 className="fw-bold m-0 text-danger">${checkerInfo?.remaining_amount?.toFixed(2) || "0.00"}</h4>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Main Content Area */}
            <Row>
                <Col xl={12}>
                    <div className="mc-card p-4 rounded-4 shadow-sm border-0 bg-white">
                        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3 border-bottom pb-3">
                            <div className="d-flex gap-2">
                                <Button
                                    variant={activeTab === "products" ? "primary" : "secondary"}
                                    onClick={() => setActiveTab("products")}
                                    className="px-4 py-2 rounded-3 fw-bold border-0 shadow-sm"
                                >
                                    <span className="material-icons align-middle me-1 fs-6">grid_view</span>
                                    {t("Checked Products")} ({products.length})
                                </Button>
                                <Button
                                    variant={activeTab === "payments" ? "primary" : "secondary"}
                                    onClick={() => setActiveTab("payments")}
                                    className="px-4 py-2 rounded-3 fw-bold border-0 shadow-sm"
                                >
                                    <span className="material-icons align-middle me-1 fs-6">receipt_long</span>
                                    {t("Payment History")} ({payments.length})
                                </Button>
                            </div>

                            {activeTab === "products" && (
                                <div style={{ maxWidth: "320px" }}>
                                    <LabelFieldComponent
                                        type="search"
                                        placeholder="Search title, code..."
                                        value={search}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                            )}
                        </div>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted fw-semibold">{t("Loading checker data...")}</p>
                            </div>
                        ) : activeTab === "products" ? (
                            /* Products Table */
                            <div className="mc-table-responsive">
                                <Table hover className="mc-table align-middle">
                                    <thead className="mc-table-head bg-light">
                                        <tr>
                                            <th style={{ minWidth: "80px" }}>Image</th>
                                            <th>Product Title</th>
                                            <th>Code</th>
                                            <th>Brand</th>
                                            <th>Store</th>
                                            <th>Category</th>
                                        </tr>
                                    </thead>
                                    <tbody className="mc-table-body">
                                        {products.length > 0 ? (
                                            products.map((product) => {
                                                const imgPath = product.card_img || product.img?.card_img || product.img?.first_img || product.first_img;
                                                const fullImgUrl = resolveImageUrl(imgPath);

                                                return (
                                                    <tr key={product.id}>
                                                        <td>
                                                            {fullImgUrl ? (
                                                                <div
                                                                    className="rounded-3 border overflow-hidden shadow-sm"
                                                                    style={{ width: "48px", height: "48px", cursor: "pointer" }}
                                                                    onClick={() => setPreviewImage(fullImgUrl)}
                                                                >
                                                                    <img
                                                                        src={fullImgUrl}
                                                                        alt={product.title}
                                                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                                        onError={(e) => { e.target.style.display = "none"; }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div
                                                                    className="rounded-3 bg-light d-flex align-items-center justify-content-center border text-muted"
                                                                    style={{ width: "48px", height: "48px" }}
                                                                >
                                                                    <span className="material-icons fs-5">image_not_supported</span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="fw-semibold text-dark text-truncate d-block" style={{ maxWidth: "260px" }}>
                                                                {product.title || "N/A"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <Badge bg="light" className="text-dark border px-2 py-1 font-monospace">
                                                                {product.product_code || "N/A"}
                                                            </Badge>
                                                        </td>
                                                        <td><span className="fw-medium text-secondary">{product.brand?.name || "N/A"}</span></td>
                                                        <td><span className="fw-medium text-secondary">{product.store?.name || "N/A"}</span></td>
                                                        <td><span className="fw-medium text-secondary">{product.category?.name || "N/A"}</span></td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="text-center py-5 text-muted">
                                                    <span className="material-icons fs-1 d-block mb-2 text-black-50">search_off</span>
                                                    {t("No products found for this checker.")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            /* Payments Table */
                            <div className="mc-table-responsive">
                                <Table hover className="mc-table align-middle">
                                    <thead className="mc-table-head bg-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Payment Date</th>
                                            <th>Amount</th>
                                            <th>Bank Details</th>
                                            <th>Created By</th>
                                            <th>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody className="mc-table-body">
                                        {payments.length > 0 ? (
                                            payments.map((payment, index) => (
                                                <tr key={payment.id}>
                                                    <td className="fw-bold text-muted">{index + 1}</td>
                                                    <td>{payment.payment_date || "N/A"}</td>
                                                    <td>
                                                        <Badge bg="success" className="fs-6 px-3 py-1">
                                                            {Number(payment.amount || 0).toFixed(2)}
                                                        </Badge>
                                                    </td>
                                                    <td><span className="fw-semibold text-dark">{payment.bank ? payment.bank.name : "N/A"}</span></td>
                                                    <td><Badge bg="info" className="text-dark">{payment.created_by?.name || "System"}</Badge></td>
                                                    <td className="text-muted">{payment.notes || "-"}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="text-center py-5 text-muted">
                                                    {t("No payments recorded yet.")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Image Modal */}
            <Modal show={!!previewImage} onHide={() => setPreviewImage(null)} centered size="md">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fs-6 text-muted">{t("Product Preview")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-3">
                    {previewImage && (
                        <img
                            src={previewImage}
                            alt="Product Preview"
                            className="img-fluid rounded-3 shadow"
                            style={{ maxHeight: "70vh", objectFit: "contain" }}
                        />
                    )}
                </Modal.Body>
            </Modal>

            {/* Add Payment Modal Component */}
            <AddPaymentModal
                show={showPaymentModal}
                onHide={() => setShowPaymentModal(false)}
                listerId={checkerId} // Sending Checker User ID
                listerInfo={checkerInfo}
                banks={banks}
                t={t}
                onSuccess={handlePaymentSuccess}
            />
        </PageLayout>
    );
}